import axios from "axios";
import {
  JACKPOT_VALUE_TO_ID,
  BLANK_SYMBOL_ID,
  ZEUS_DOUBLE_ID,
  ZEUS_MYSTERY_ID,
  ZEUS_COLLECT_ID,
  CD_MULT_ID,
  CM_MINOR_ID,
  CM_MINI_ID,
  CM_MAJOR_ID,
  COIN_SYMBOL_IDS,
  BONUS_SYMBOL_IDS,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import StorageManager from "../../../utils/StorageManager";
import { PROVIDER_CONFIG } from "./providerConfig";

// Parse bonus screen symbol: "0" → 0, "coin_10" → 20, "coin_25" → 27 (jackpot)
function parseBonusSymbol(symbolId) {
  if (typeof symbolId === "number") return symbolId;
  if (typeof symbolId !== "string") return 0;

  if (symbolId.startsWith("coin_")) {
    const coinValue = parseInt(symbolId.slice(5), 10);
    if (isNaN(coinValue)) return 0;
    if (JACKPOT_VALUE_TO_ID[coinValue] !== undefined) {
      return JACKPOT_VALUE_TO_ID[coinValue];
    }
    return 10 + coinValue;
  }

  const n = parseInt(symbolId, 10);
  return isNaN(n) ? 0 : n;
}

// Coin-win positions for PC trigger (Zeus orbs + regular coins) — same for live spin + init reconstruct
const TRIGGER_COIN_WIN_IDS = new Set([
  ...BONUS_SYMBOL_IDS,
  ...COIN_SYMBOL_IDS,
  CD_MULT_ID,
  CM_MINOR_ID,
  CM_MINI_ID,
  CM_MAJOR_ID,
]);

function collectTriggerCoinWinPositions(screen) {
  const coords = [];
  if (!Array.isArray(screen)) return coords;
  for (let reel = 0; reel < screen.length; reel++) {
    const reelData = screen[reel];
    if (!Array.isArray(reelData)) continue;
    for (let row = 0; row < reelData.length; row++) {
      const raw = reelData[row];
      const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
      if (!isNaN(id) && TRIGGER_COIN_WIN_IDS.has(id)) {
        coords.push([reel, row]);
      }
    }
  }
  return coords;
}

function buildSyntheticCoinWin(positions) {
  if (!positions.length) return null;
  return {
    id: null,
    win: 0,
    positions,
    symbol: null,
    isCoin: true,
  };
}

// Convert API screen (string IDs) → numeric symbol IDs through SYMBOL_MAP
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) return screenData;
  const map = PROVIDER_CONFIG.SYMBOL_MAP;
  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const n = parseBonusSymbol(symbolId);
      return map[n] !== undefined ? map[n] : n;
    });
  });
}

// Web Worker factory
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

// Inline spin worker — runs API calls off the main thread
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature, purchasedFeatureLevel, options } = event.data;

    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "PopiPlay: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      // Build request body
      let payloadOptions = { bet: bet, ...(options || {}) };
      if (purchasedFeature) {
        payloadOptions.purchased_feature = purchasedFeature;
      }
      if (purchasedFeatureLevel) {
        payloadOptions.purchased_feature_level = String(purchasedFeatureLevel);
      }
      const requestBody = { command: command, options: payloadOptions };

      const response = await axios.post(
        apiUrl,
        requestBody,
        {
          headers: {
            [csrfTokenName]: csrfTokenValue,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.data) {
        postMessage({ error: "PopiPlay: Spin API call failed: Invalid response structure" });
        return;
      }

      if (response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        postMessage({ error: response.data });
        return;
      }

      postMessage(response.data);

    } catch (error) {
      if (error.response?.data?.errors) {
        postMessage({ error: error.response.data });
      } else {
        postMessage({ error: "PopiPlay: Spin API call failed: " + error.message });
      }
    }
  };
`;

// ─────────────────────────────────────────────
// PopiPlay RGS — 3 Orbs of Zeus
// ─────────────────────────────────────────────

class PopiPlayRGS {
  constructor() {
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null;
    this.currentToken = null;
    this.lines = null;
    // Replay mode (docs/replay-history-spec.md §0). Step-cursor fields
    // (replayStepIds/replayMaxStepId/replayStepCursor/replayLastStepId/
    // replayBet) are populated by fetchReplayInit() and consumed only by
    // _replaySpin()/_nextReplayStepId() below — self-contained on this
    // object so spin()'s replay branch needs no new params threaded through
    // the shared rgsPlay/getSpinParams call chain every live spin also uses.
    this.replayMode = false;
    this.replayUrl = null;
    this.replayStepIds = null;
    this.replayMaxStepId = null;
    this.replayStepCursor = 0;
    this.replayLastStepId = null;
    this.replayBet = null;
  }

  // ── Win Classification ──

  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);
    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }
    return "regular_win";
  }

  // ── Win Normalization ──

  // Match row positions against init line definitions to find line ID
  resolveLineId(positions) {
    if (!this.lines || !Array.isArray(positions)) return null;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines[i];
      if (!Array.isArray(line) || line.length !== positions.length) continue;
      let match = true;
      for (let j = 0; j < positions.length; j++) {
        if (line[j] !== positions[j]) {
          match = false;
          break;
        }
      }
      if (match) return i;
    }
    return null;
  }

  // Convert raw API wins to state format
  // API formats:
  //   Line win:  ["line", amount, [row0, row1, ...], lineId]
  //   Coin win:  ["coin", value, reel, row]
  //   Bonus:     ["coins_game", totalWin]
  //   Jackpot:   ["jackpot", value]
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple)) return null;

        // Coin win: individual coin payout
        if (winTuple[0] === "coin") {
          const [, value, reel, row] = winTuple;
          const rawSymbol = screen?.[reel]?.[row];
          let mappedSymbol = null;
          if (rawSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol]
              : (typeof rawSymbol === "string" ? parseInt(rawSymbol, 10) : rawSymbol);
          }
          return {
            id: null,
            win: value || 0,
            positions: [[reel, row]],
            symbol: mappedSymbol,
            isCoin: true,
          };
        }

        // Bonus completion: total coins_game win
        if (winTuple[0] === "coins_game") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isBonus: true,
          };
        }

        // Grand jackpot
        if (winTuple[0] === "jackpot") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isJackpot: true,
            jackpotType: "grand",
          };
        }

        // Line win: ["line", amount, [rowPositions], lineId]
        if (winTuple.length < 4) return null;
        const [, amount, positions, lineId] = winTuple;
        if (!Array.isArray(positions)) return null;

        const transformedPositions = positions.map((rowValue, reelIndex) => {
          return [reelIndex, rowValue];
        });

        // Derive winning symbol — pick first non-wild from the screen
        let mappedSymbol = null;
        for (let p = 0; p < positions.length; p++) {
          const row = positions[p];
          const screenSymbol = screen?.[p]?.[row];
          if (screenSymbol !== undefined) {
            const mapped = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
            if (mapped !== PROVIDER_CONFIG.WILD_SYMBOL_ID) {
              mappedSymbol = mapped;
              break;
            }
          }
        }
        // Fallback: all wilds — use first symbol
        if (mappedSymbol === null && positions.length > 0) {
          const row = positions[0];
          const screenSymbol = screen?.[0]?.[row];
          if (screenSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
          }
        }

        return {
          id: lineId,
          win: amount,
          positions: transformedPositions,
          symbol: mappedSymbol,
        };
      })
      .filter((w) => w !== null);
  }

  // ── Adapt Round to State ──

  adaptSpinRoundToState(normalizedOutcome, bet) {
    if (!normalizedOutcome) return null;

    const totalWin = normalizedOutcome.win || 0;
    const winType = this.computeWinType(totalWin, bet);

    const round = {
      reels: normalizedOutcome.reels || [],
      wins: normalizedOutcome.wins || [],
      win: totalWin,
      bet,
      winType,
      isAnimatedWin: winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
      storage: normalizedOutcome.storage || null,
    };

    if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED) {
      round.anticipation = FEATURES_CONFIG.TENSION.ANTICIPATION.FORCE
        ? true
        : (normalizedOutcome.anticipation || false);
    }

    return round;
  }

  // ── Response Handler ──

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: () => false,
            extractFreeSpins: () => [],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) return {};
            const transformedOutcome = { ...outcome };
            transformedOutcome.wins = Array.isArray(outcome.wins)
              ? this.normalizeWins(outcome.wins, outcome.screen)
              : [];
            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "balance.game",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  // ── Init Game ──

  async initGame(useApiData = false) {
    if (!window.__OPTIONS__) {
      throw new Error("PopiPlay: window.__OPTIONS__ not found.");
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`
      );
    }

    try {
      const response = await axios.post(
        apiUrl,
        { command: "init" },
        {
          headers: {
            [csrfHeaderName]: csrfHeaderValue,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data) {
        throw new Error("PopiPlay: Invalid API response - response.data is missing");
      }

      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const featureMultipliers =
        response.data?.options?.feature_options?.feature_multipliers;

      const jackpotMultipliers =
        response.data?.options?.jackpots ||
        response.data?.options?.feature_options?.jackpots ||
        null;

      const coinValues = response.data?.options?.coin_values || null;
      const progress = response.data?.progress || null;

      // Validate required fields
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error("PopiPlay: Invalid API response - options.available_bets is missing or invalid");
      }
      if (typeof defaultBet !== "number") {
        throw new Error("PopiPlay: Invalid API response - options.default_bet is missing or invalid");
      }
      if (typeof baseBet !== "number") {
        throw new Error("PopiPlay: Invalid API response - options.base_bet is missing or invalid");
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error("PopiPlay: Invalid API response - options.paytable is missing or invalid");
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error("PopiPlay: Invalid API response - options.lines is missing or invalid");
      }
      this.lines = lines;
      if (typeof wallet !== "number") {
        throw new Error("PopiPlay: Invalid API response - balance.wallet is missing or invalid");
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error("PopiPlay: Invalid API response - options.currency.code is missing or invalid");
      }

      // Bonus buy multipliers from feature_options
      // Formula: currentBet * feature_multiplier / 100 = buy bonus price (in subunits)
      // Divide raw API values by 100 to get the multiplier applied to currentBet.
      // Example: bonus_buy=8000 → 8000/100 = 80× multiplier
      //          EUR bet=100 (1.00€) → 100 * 80 = 8000 subunits = 80.00€
      //          mBTC bet=1000 → 1000 * 80 = 80000 subunits = 0.8000 mBTC
      // Handles sim format { bonus_buy: 2000 } and real format { bonus_buy: { 1: 8000, 2: 30000 } }
      let bonusBuyMultiplier;
      let bonusBuyExtraMultiplier;

      if (featureMultipliers?.bonus_buy) {
        if (typeof featureMultipliers.bonus_buy === "object") {
          const level1Cost = featureMultipliers.bonus_buy["1"] || featureMultipliers.bonus_buy[1];
          const level2Cost = featureMultipliers.bonus_buy["2"] || featureMultipliers.bonus_buy[2];
          bonusBuyMultiplier = level1Cost ? level1Cost / 100 : undefined;
          bonusBuyExtraMultiplier = level2Cost ? level2Cost / 100 : undefined;
        } else {
          bonusBuyMultiplier = featureMultipliers.bonus_buy / 100;
          bonusBuyExtraMultiplier = featureMultipliers.bonus_buy_extra
            ? featureMultipliers.bonus_buy_extra / 100
            : undefined;
        }
      }

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };


      let featureState = this.detectFeatureState(response.data);
      const storedState = this.loadGameState(response.data);

      // ── Completion Reload: bonus finished but localStorage not yet cleared ──
      // After bonus ends, the API returns a normal base game init (no bonus data).
      // But localStorage still has the full bonus state until the user presses spin.
      // Synthesize a completion featureState so the game replays the collection.
      // Skip when useApiData (error recovery): stale bonus LS on another tab must NOT
      // force type:"bonus" or getSpinParams keeps sending respin after dismiss.
      if (
        !useApiData &&
        featureState.type !== "bonus" &&
        StorageManager.hasBonusGameRound()
      ) {
        const savedRound = StorageManager.loadBonusGameRound();
        if (savedRound) {
          featureState = {
            type: "bonus",
            phase: "completion",
            data: {
              respinGameType: savedRound.respinGameType || "",
              currentRound: savedRound.round,
              initialRespinScreen: savedRound.round?.reels || savedRound.initialRespinScreen,
              initialRespinValues: savedRound.valuesGrid || savedRound.initialRespinValues,
              initialRespinOriginalValues: savedRound.originalValues || savedRound.initialRespinOriginalValues,
              valuesGrid: savedRound.valuesGrid,
              originalValues: savedRound.originalValues,
              prevScreen: savedRound.prevScreen,
              respinsLeft: savedRound.respinsLeft,
              respinsIssued: savedRound.respinsIssued,
              coinCount: savedRound.coinCount || 0,
              totalWin: savedRound.totalWin || 0,
              triggerCoordinates: savedRound.triggerCoordinates,
              initialEvents: savedRound.initialEvents || [],
              flippedCoinPositions: savedRound.flippedCoinPositions || [],
              screenProgress: [],
              isReload: true,
            },
          };
        }
      }

      // On reload during bonus, attach round data for grid restoration
      if (
        featureState.type === "bonus" &&
        (featureState.phase === "in_progress" || featureState.phase === "intro")
      ) {
        const outcome = response.data?.outcome;
        if (outcome?.screen && Array.isArray(outcome.screen)) {
          const normalizedScreen = normalizeScreenData(outcome.screen);
          featureState.data.currentRound = {
            reels: normalizedScreen,
            wins: outcome.wins || [],
            win: response.data?.balance?.game || 0,
            storage: outcome.storage || {},
          };
          if (featureState.phase === "in_progress") {
            featureState.data.isReload = true;
            // Ensure trigger_win key exists for WinDisplay on reload.
            // If not already set (from live play), bootstrap with balance.game.
            const existingTriggerWin = StorageManager.loadTriggerWin();
            if (!existingTriggerWin) {
              StorageManager.saveTriggerWin(response.data?.balance?.game || 0);
            }
          }
        }
      }

      // ── Reconstruct base game round from features.trigger_screen during bonus ──
      // Cold load: when LS missing. Error recovery (useApiData): always overwrite stale LS
      // with server trigger screen — extractRoundFromInit only has options.screen (wrong).
      // Bonus intro: same overwrite even without useApiData — stale LS from a previous
      // game on this browser (e.g. mobile→desktop) must not mix with the new intro.
      const forceBonusIntroFromApi =
        featureState.type === "bonus" && featureState.phase === "intro";

      if (forceBonusIntroFromApi) {
        const hadStaleLs =
          StorageManager.hasBonusGameRound() || StorageManager.hasBaseGameRound();
        if (hadStaleLs) {
          StorageManager.clearBonusGameRound();
          StorageManager.clearTriggerWin();
          StorageManager.clearBaseGameRound();
        }
      }

      let reconstructedBonusRound = null;
      if (
        featureState.type === "bonus" &&
        (featureState.phase === "intro" || featureState.phase === "in_progress") &&
        (!StorageManager.hasBaseGameRound() || useApiData || forceBonusIntroFromApi)
      ) {
          const triggerScreen = response.data?.features?.trigger_screen || response.data?.outcome?.screen;
        if (triggerScreen && Array.isArray(triggerScreen)) {
          const normalizedReels = normalizeScreenData(triggerScreen);

          // Intro: full trigger round with coin wins (needed for entry animation).
          // In-progress: stripped — first respin already cleared wins; only reels matter.
          const isIntro = featureState.phase === "intro";

          // Same as live trigger LS: every Zeus/coin on the BASE trigger screen
          // (trigger_coordinates alone omits regular coins / can miss green Double).
          const coinPositions = collectTriggerCoinWinPositions(normalizedReels);
          const coinWin = isIntro ? buildSyntheticCoinWin(coinPositions) : null;
          const syntheticWins = coinWin ? [coinWin] : [];

          const outcomeBet = response.data?.outcome?.bet || defaultBet;
          const outcomeWin = response.data?.outcome?.win || 0;
          const winType = isIntro ? this.computeWinType(outcomeWin, outcomeBet) : "";

          reconstructedBonusRound = {
            id: response.data?.flow?.round_id ?? `pc-${featureState.phase}`,
            reels: normalizedReels,
            wins: syntheticWins,
            win: outcomeWin,
            bet: outcomeBet,
            winType,
            isAnimatedWin: isIntro && winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
            triggerWin: isIntro, // Only true for intro — in_progress is already stripped
          };

          StorageManager.saveBaseGameRound({ round: reconstructedBonusRound, bet: outcomeBet });
        }
      }

      const balance = wallet;
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      // useApiData / forced bonus intro reconstruct → prefer trigger round (never stale LS).
      // useApiData + base/FR → extractRoundFromInit (options.screen) as before.
      const round =
        useApiData || forceBonusIntroFromApi
          ? (reconstructedBonusRound || this.extractRoundFromInit(response.data))
          : storedState.round || savedBaseGame?.round || this.extractRoundFromInit(response.data);

      const gameMode = this.getGameMode(featureState);

      // ── Free Rounds detection ──
      // PopiPlay sends free rounds as a top-level `free_rounds` object
      // and cumulative win in `balance.free_rounds`
      const freeRoundsData = response.data?.free_rounds || null;
      const freeRoundsTotalWin = response.data?.balance?.free_rounds ?? 0;

      const result = {
        balance,
        bet,
        baseBet,
        bets: availableBets,
        round,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier,
        bonusBuyExtraMultiplier,
        game: identifier,
        gameMode,
        featureState,
        reloadState: storedState.reloadState,
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        freeRounds: freeRoundsData,
        freeRoundsTotalWin,
        command: "init",
        jackpotMultipliers,
        coinValues,
        progress,
      };

      // Persist base game on first load
      if (
        result.gameMode === "base" &&
        result.round &&
        (!StorageManager.hasBaseGameRound() || useApiData)
      ) {
        StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
      }

      // Persist free rounds state on init (for reload recovery)
      if (freeRoundsData?.status === "active") {
        if (StorageManager.hasFreeRoundsRound()) {
          // Exists → update changing fields, never touch betToRestore
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
          });
        } else {
          // Doesn't exist → create with betToRestore from base_game_round
          // savedBaseGame was loaded BEFORE base_game_round gets overwritten (line 568)
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
            betToRestore: savedBaseGame?.bet ?? result.bet,
          });
        }

        // ── FR round 0: reset base game to fresh neutral state ──
        // No rounds played yet — use fresh API reels with no wins.
        // Prevents stale base game win from showing on reload.
        if (freeRoundsData.performed === 0) {
          // Clear stale bonus game data — if FR was assigned during a bonus,
          // the bonus is finished but localStorage wasn't cleared (no base spin
          // happened between bonus end and FR activation).
          StorageManager.clearBonusGameRound();

          const freshRound = this.extractRoundFromInit(response.data);
          if (freshRound) {
            StorageManager.saveBaseGameRound({
              round: freshRound,
              bet: savedBaseGame?.bet ?? result.bet,
              balance: result.balance,
            });
            // Also update the result so the current session uses fresh reels
            result.round = freshRound;
          }
        }
      }

      // ── FR canceled externally (operator revoked during bonus or base game) ──
      // Throw 206 immediately to trigger error recovery overlay.
      // This resets the entire game state and pushes the user to a clean base game.
      if (freeRoundsData?.status === "canceled") {
        StorageManager.clearFreeRoundsRound();
        StorageManager.clearBonusGameRound();
        const cancelError = {
          errors: [{ code: 206, desc: "free_rounds_canceled" }],
        };
        throw cancelError;
      }

      // ── FR Completion Reload: FR finished but localStorage not yet cleared ──
      // After FR ends, the API returns no free_rounds (base game init).
      // But localStorage still has FR data until the user plays a base game spin.
      // Synthesize a "played" status so the slice sets completionPending
      // and the hook replays the completion popups.
      // EXCEPTION: If performed < count, the FR was canceled externally (operator),
      // not completed — clear stale localStorage instead of showing completion.
      if (
        !freeRoundsData?.status &&
        StorageManager.hasFreeRoundsRound()
      ) {
        const savedFR = StorageManager.loadFreeRoundsRound();
        if (savedFR) {
          const wasCompleted = savedFR.performed >= savedFR.count;
          if (wasCompleted) {
            // Legitimate completion — synthesize "played" for completion popups
            result.freeRounds = {
              status: "played",
              bet_value: savedFR.betValue,
              performed: savedFR.performed,
              count: savedFR.count,
              // Tells the slice whether `win` below already includes the last
              // round's own win (reveal played before reload) or not (reload
              // interrupted the reveal) — decides if withholding is valid.
              lastRoundApplied: savedFR.lastRoundApplied ?? true,
            };
            result.freeRoundsTotalWin = savedFR.win ?? 0;
          } else {
            // External cancellation — clear stale localStorage, start fresh
            StorageManager.clearFreeRoundsRound();
          }
        }
      }

      return result;
    } catch (error) {
      // Structured errors (from API or synthesized 206) — pass through as-is
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Init API call failed: ${error.message}`);
    }
  }

  // ── Replay Session (docs/replay-history-spec.md §0) ──
  //
  // A pure on/off switch on this same object, deliberately NOT a
  // parallel adapter — every other feature here (Hold&Win, Free Spins, Pot
  // Collection, Bonus Buy) already shares this one initGame()/spin() pair,
  // told apart by state/params rather than a swapped adapter, so replay
  // follows the same shape and the existing bonus/free-spin detection logic
  // (detectFeatureState) keeps working with zero new code.
  //
  // initGame() itself is untouched (Stage 1 uses a separate lightweight
  // fetchReplayInit() instead, see below). spin() and getBalanceStrategy()
  // both branch on this.replayMode near the top of their existing bodies —
  // neither's live-mode logic below was otherwise changed.

  /**
   * Switch this object into replay mode for the given round's replay API
   * base URL (the `replayUrl` extracted from a history row's `replay_link`,
   * see _extractReplayUrl below).
   * @param {string} replayUrl
   */
  enterReplayMode(replayUrl) {
    this.replayMode = true;
    this.replayUrl = replayUrl;
  }

  /**
   * Switch this object back to live mode. Does not by itself restore live
   * game state — that's Stage 5 (a fresh lightweight fetch via initGame()),
   * triggered from the History board's own close, not from here.
   */
  exitReplayMode() {
    this.replayMode = false;
    this.replayUrl = null;
    this.replayStepIds = null;
    this.replayMaxStepId = null;
    this.replayStepCursor = 0;
    this.replayLastStepId = null;
    this.replayBet = null;
  }

  /**
   * Stage 1 — fetch the replay round's step sequence (step_ids/max_step_id,
   * for replaySlice's step cursor), its own bet, and its own wallet snapshot
   * — both informational display only (spec §9: never merged into the live
   * wallet/bet, which stay completely untouched in state.game the whole time
   * replay is active; the UI just displays these instead while active).
   *
   * Deliberately lightweight: unlike the live boot sequence (initGame),
   * this does NOT reload assets/sounds, does NOT re-parse the full game
   * config into Redux, and does NOT touch localStorage. The live game's
   * config (paytable, reels, etc.) is already loaded and identical for a
   * replay of the same game — all replay actually needs from init is the
   * step sequence. initGame() itself stays completely untouched by this.
   *
   * Confirmed live against this game (docs/replay-history-spec.md §0): a
   * plain base-game round has step_ids:[1]; a bonus_buy round had
   * step_ids:[1,2,3,4,5,6,7] — one step per real gameplay action that
   * round took, base spin included.
   *
   * @returns {Promise<{stepIds: number[]|null, maxStepId: number|null, bet: number|null, balance: number|null}>}
   */
  async fetchReplayInit() {
    if (!this.replayUrl) {
      throw new Error("PopiPlay: not in replay mode (call enterReplayMode first)");
    }

    try {
      const response = await axios.get(this.replayUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const stepIds = Array.isArray(data?.step_ids) ? data.step_ids : null;
      const maxStepId =
        typeof data?.max_step_id === "number" ? data.max_step_id : null;
      const bet = typeof data?.bet === "number" ? data.bet : null;
      const balance =
        typeof data?.balance?.wallet === "number" ? data.balance.wallet : null;
      // Stash on this — _replaySpin()/_nextReplayStepId() below read from
      // here, not from Redux (this object has no Redux access, and keeping
      // the cursor self-contained means spin()'s replay branch needs no new
      // params threaded through the shared rgsPlay call chain).
      this.replayStepIds = stepIds;
      this.replayMaxStepId = maxStepId;
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      this.replayBet = bet;

      // Free rounds / casino rounds — the init response reports the campaign
      // state AS IT WAS at the start of this round (verified live 2026-08-11:
      // `free_rounds:{status:"active", bet_value, performed, count}` plus the
      // running total in `balance.free_rounds`). Returned here so the FR
      // counter and Total Win are on screen the moment the Enter modal closes,
      // rather than only appearing once the first replay step is fetched.
      const freeRounds = data?.free_rounds || null;
      const freeRoundsTotalWin = data?.balance?.free_rounds ?? 0;

      // POT stages AS THEY WERE at the start of this round — same idea as
      // free_rounds above, and the same source a live spin reads
      // (`progress.pots`, see gameSettingsSlice's rgsPlay.fulfilled). Captured
      // and verified for this game; see replay-history-spec.md §0.
      //
      // Returned so the orbs can jump to the replayed round's stages the moment
      // the Enter modal closes. Without it the player watches an old round with
      // TODAY'S pots standing behind it, which is a different round's state on
      // screen at the same time.
      const pots = data?.progress?.pots || null;

      return {
        stepIds,
        maxStepId,
        bet,
        balance,
        freeRounds,
        freeRoundsTotalWin,
        pots,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Replay init call failed: ${error.message}`);
    }
  }

  /**
   * Standalone replay link boot (docs/replay-history-spec.md §0, Stage 9) —
   * used ONLY when the page itself was launched as a replay link
   * (`window.__OPTIONS__.server_id === "replay"`, see
   * launcherOptions.isStandaloneReplayMode()). There is no live session to
   * validate or restore in this mode, so unlike initGame() above this:
   *  - GETs `replayUrl` directly (no POST, no play_token/CSRF headers —
   *    confirmed live 2026-08-10 that both are empty in this launch mode's
   *    __OPTIONS__, so initGame()'s own missing-fields check would throw
   *    immediately if called here instead).
   *  - Never touches StorageManager — none of the live reload-recovery
   *    concerns baked into initGame() (mid-bonus respin, free rounds,
   *    base-game round persistence) apply to a replay session; every page
   *    load starts fresh at step one (confirmed §0: "no localStorage
   *    involvement at all" for replay).
   *  - Puts this object straight into replay mode and primes the step
   *    cursor itself (the same fields fetchReplayInit sets) — this single
   *    call does the equivalent of enterReplayMode()+fetchReplayInit() from
   *    the in-game hot-swap path, plus the full config parse initGame()
   *    would otherwise have done, in one request.
   *
   * Returns the same result shape initGame() does, so gameSettingsSlice and
   * every sibling reducer on initializeGame.fulfilled (freeRoundsSlice,
   * spinSlice, potCollectionSlice, featureGameSettingsSlice) work completely
   * unchanged. featureState/round intentionally reflect a fresh base-game
   * boot with no outcome yet — the init response never has one
   * (docs/replay-history-data-contract.md §2, confirmed live); the first
   * replay spin's own _replaySpin() supplies the real featureState/round
   * once fetched, exactly like a live init→first-spin handoff.
   *
   * @param {string} replayUrl
   * @returns {Promise<object>} same result shape as initGame()
   */
  async initFromReplayLink(replayUrl) {
    if (!replayUrl) {
      throw new Error("PopiPlay: standalone replay link has no replayUrl");
    }

    try {
      const response = await axios.get(replayUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (!data || typeof data !== "object") {
        throw new Error("PopiPlay: Invalid replay init response");
      }
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const options = data.options;
      const availableBets = options?.available_bets;
      const defaultBet = options?.default_bet;
      const baseBet = options?.base_bet;
      const paytable = options?.paytable;
      const lines = options?.lines;
      const currencyObj = options?.currency;
      const wallet = data.balance?.wallet;
      const jackpotMultipliers =
        options?.jackpots || options?.feature_options?.jackpots || null;
      const coinValues = options?.coin_values || null;

      // NOTE: options.default_bet/base_bet are NOT hard-required here, unlike
      // initGame()'s live validation — the confirmed replay init contract
      // (docs/replay-history-data-contract.md §2) only lists available_bets/
      // paytable/reels/screen/lines/jackpots/coin_values/feature_options/
      // special_symbols/layout/currency under options; default_bet/base_bet
      // are live-boot concepts ("what bet does a NEW player see") that a
      // replay init response may simply omit — a replay already has its own
      // bet from the top-level `bet` field. gameSettingsSlice.js itself
      // already tolerates a missing baseBet (`result.baseBet || 1`), so this
      // matches that tolerance instead of live initGame()'s stricter check.
      const missingFields = [];
      if (!availableBets || !Array.isArray(availableBets)) missingFields.push("options.available_bets");
      if (!paytable || typeof paytable !== "object") missingFields.push("options.paytable");
      if (!lines || !Array.isArray(lines)) missingFields.push("options.lines");
      if (typeof wallet !== "number") missingFields.push("balance.wallet");
      if (!currencyObj || typeof currencyObj.code !== "string") missingFields.push("options.currency.code");
      if (missingFields.length > 0) {
        throw new Error(`PopiPlay: Invalid replay init response — missing [${missingFields.join(", ")}]`);
      }

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };

      // Prefer the round's own bet (top-level, always present per the
      // confirmed contract) — defaultBet is only a fallback for a malformed
      // response, baseBet defaults like gameSettingsSlice.js's own `|| 1`.
      const bet =
        typeof data.bet === "number"
          ? data.bet
          : typeof defaultBet === "number"
            ? defaultBet
            : 0;
      const safeBaseBet = typeof baseBet === "number" ? baseBet : 1;
      const round = this.extractRoundFromInit(data);

      // Prime replay mode + step cursor directly — equivalent of
      // enterReplayMode()+fetchReplayInit() for the in-game path (§0 Stage 0/1).
      this.replayMode = true;
      this.replayUrl = replayUrl;
      this.replayStepIds = Array.isArray(data.step_ids) ? data.step_ids : null;
      this.replayMaxStepId = typeof data.max_step_id === "number" ? data.max_step_id : null;
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      this.replayBet = bet;

      return {
        balance: wallet,
        bet,
        baseBet: safeBaseBet,
        bets: availableBets,
        round,
        token: null,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier: undefined,
        bonusBuyExtraMultiplier: undefined,
        game: window.__OPTIONS__?.identifier,
        gameMode: "base",
        featureState: { type: null, phase: null, data: null },
        reloadState: { needsReload: false },
        availableActions: data.flow?.available_actions || ["init", "spin"],
        flowState: data.flow?.state || "ready",
        features: data.features || null,
        // Free rounds are deliberately NOT surfaced here. This result is
        // consumed by initializeGame.fulfilled — freeRoundsSlice's LIVE init
        // reducer, which reads the player's real StorageManager, sets
        // reloadPending, and on status "played" raises completionPending,
        // which would fire the campaign's completion popups at boot before a
        // single step has played. The standalone boot dispatches
        // fetchReplayInit right after this, and its replay-specific reducer
        // seeds the same indicators without any of that live bookkeeping.
        freeRounds: null,
        freeRoundsTotalWin: 0,
        command: "init",
        jackpotMultipliers,
        coinValues,
        progress: data.progress || null,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) throw error;
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Standalone replay init failed: ${error.message}`);
    }
  }

  /**
   * The step id to fetch next, or null once the round has no more steps.
   * Mirrors selectNextReplayStepId in replaySlice.js exactly (spec §6.2
   * precedence) — that copy is the UI-facing "are we done" signal for the
   * future Ended-modal trigger (Stage 4/5), this one is what _replaySpin()
   * actually acts on. Two copies because this object has no Redux access;
   * if they ever need to drift apart, that's a smell — keep them in sync.
   */
  _nextReplayStepId() {
    const { replayStepIds, replayStepCursor, replayMaxStepId, replayLastStepId } = this;

    if (Array.isArray(replayStepIds)) {
      return replayStepCursor < replayStepIds.length
        ? replayStepIds[replayStepCursor]
        : null;
    }
    if (typeof replayMaxStepId === "number" && typeof replayLastStepId === "number") {
      return replayLastStepId < replayMaxStepId ? replayLastStepId + 1 : null;
    }
    return replayLastStepId == null ? 1 : null;
  }

  /** Called after a successful replay step fetch to move the cursor forward. */
  _advanceReplayStep(stepId) {
    if (Array.isArray(this.replayStepIds)) {
      this.replayStepCursor += 1;
    }
    this.replayLastStepId = stepId;
  }

  /**
   * Stage 2/3 — replay's answer to spin(). Fetches the next recorded step
   * (GET {replayUrl}/{stepId}) instead of doing a real spin, and runs it
   * through the SAME core transform pipeline the live path uses
   * (normalizeScreenData → extractRound → detectFeatureState →
   * adaptSpinRoundToState) so downstream consumers (gameSettingsSlice,
   * potCollectionSlice, featureGameSettingsSlice, ReelControlContext's
   * stop-detection effect) treat the result identically to a live spin.
   *
   * Deliberately does NOT replicate, on purpose:
   * - Any StorageManager read/write (spec §9 — replay must never touch the
   *   real save/reload state; this is the one live-path behavior that
   *   would be actively harmful to carry over, not just unnecessary).
   * - Free-rounds-specific bookkeeping — live-only state tracking.
   * - this.currentToken — never overwritten here, so a live spin right
   *   after exiting replay isn't sent with a stale replay round_id.
   *
   * The bonus-trigger synthetic coin win IS replicated (see below). It was
   * skipped on the first pass as "cosmetic"; that was wrong — it's what
   * carries the Pot Collection intro popup, so without it a replayed bonus
   * round shows its trigger screen and then never enters the bonus.
   *
   * RESOLVED (2026-08-10, real bonus_buy replay step inspected):
   * detectFeatureState()'s bonus-phase check needs
   * features.respins_left/respin_game_type on top of flow.state, and a
   * replay step DOES carry all three (`respins_left:3`,
   * `respin_game_type:"DMC"`, `flow.state:"respin"`, `flow.command:"spin"`)
   * alongside the full trigger payload (initial_respin_screen/_values/
   * _original_values, trigger_coordinates, initial_events, initial_coins_count).
   * So bonus phase detection on replay steps works exactly as it does live.
   *
   * @returns {Promise<object>} shaped like a live spin() result
   */
  async _replaySpin() {
    let stepId = this._nextReplayStepId();
    if (stepId == null) {
      // Cursor exhausted, but a spin still arrived — wrap back to step one
      // rather than throwing. Throwing here freezes the reels: they were
      // already started by handleSpin, and a rejected rgsPlay never delivers
      // stop positions, so the game looks permanently stuck with no way out.
      //
      // Wrapping is also exactly the intended semantics for the standalone
      // replay link (§0 Stage 9): that page loops the one round forever, and
      // its "Replay Again" resets this cursor via fetchReplayInit — so any
      // spin arriving before that async reset lands (or after it failed)
      // should simply restart the round, which is what the player asked for.
      //
      // Never reached on the in-game hot-swap path in normal operation:
      // handleSpin's replayFinished guard opens the Ended modal instead of
      // spinning once the steps run out. This is the safety net for the
      // race, not the primary flow.
      this.replayStepCursor = 0;
      this.replayLastStepId = null;
      stepId = this._nextReplayStepId();
      if (stepId == null) {
        throw new Error("PopiPlay: replay round has no steps to play");
      }
    }

    try {
      const response = await axios.get(`${this.replayUrl}/${stepId}`, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (!data || typeof data !== "object") {
        throw new Error("PopiPlay: Replay step returned an invalid response");
      }
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      if (data.outcome && Array.isArray(data.outcome.screen)) {
        const normalized = normalizeScreenData(data.outcome.screen);
        data.outcome.reels = normalized;
        data.outcome.screen = normalized;
      }

      let normalizedOutcome = data.outcome;
      if (data.outcome) {
        const handler = this.getResponseHandler();
        normalizedOutcome = handler.patterns.regular.extractRound(data.outcome);
        normalizedOutcome.win = data.balance?.game ?? normalizedOutcome.win;
      }

      // Round id — same as the live path (normalizedOutcome.id below the live
      // transform). A step reports a short numeric flow.round_id; the Ended
      // modal shows it in place of a played-at date on standalone links,
      // which have no history row to take a timestamp from.
      if (normalizedOutcome && data.flow?.round_id != null) {
        normalizedOutcome.id = data.flow.round_id;
      }

      const anticipation = data.features?.anticipation;
      if (normalizedOutcome && anticipation !== undefined) {
        normalizedOutcome.anticipation = anticipation;
      }

      const featureState = this.detectFeatureState(data);
      const gameMode = this.getGameMode(featureState);
      const roundBet = data.outcome?.bet ?? this.replayBet ?? 0;
      const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, roundBet);
      // adaptSpinRoundToState builds a fresh round object and doesn't carry
      // `id` through (the live path never needs it past that point), but the
      // adapted round is what's returned below — so re-stamp it here rather
      // than widening the shared adapter, which every provider uses.
      if (adaptedRound && normalizedOutcome?.id != null) {
        adaptedRound.id = normalizedOutcome.id;
      }
      // Same condition live spin() uses (RGS.js isBonusTrigger), sourced from
      // this step's own recorded command instead of a live request's command
      // param: a bonus-trigger step commonly wins 0 immediately (payout
      // accrues over the respins that follow), so `win > 0` alone would miss
      // it — useWinController needs triggerWin to fire the reveal regardless.
      const isBonusTrigger =
        featureState.type === "bonus" &&
        featureState.phase === "intro" &&
        data.flow?.command === "spin";
      const isBonusRespin =
        featureState.type === "bonus" && data.flow?.command === "respin";

      // Attach the step's own round to featureState — potCollectionSlice reads
      // featureState.data.currentRound on both the trigger and every respin
      // (rgsPlay.fulfilled) to seed/advance the bonus grid. Mirrors the live
      // path; `data.outcome.screen` was already normalized in place above, so
      // it's used directly rather than re-normalized.
      if (featureState.type === "bonus" && (isBonusTrigger || isBonusRespin)) {
        const stepScreen = data.outcome?.screen;
        if (Array.isArray(stepScreen)) {
          featureState.data.currentRound = {
            reels: stepScreen,
            wins: normalizedOutcome?.wins || [],
            win: normalizedOutcome?.win || 0,
            bet: normalizedOutcome?.bet,
            storage: data.outcome?.storage || null,
          };
        }
      }

      // Synthetic coin win on the trigger step — NOT cosmetic, load-bearing.
      // useWinController only opens the Pot Collection intro popup from inside
      // a win whose `isCoin` is true (see its POT COLLECTION TRIGGER block),
      // and that popup closing is what starts the cloud transition into the
      // bonus scene. A trigger step reports `outcome.wins: []` (verified live
      // 2026-08-10 — the payout accrues over the respins), so without
      // manufacturing this win the trigger screen renders and the game never
      // enters the bonus at all. Same helper and fallback chain as the live
      // path, and as initGame's own reload reconstruction.
      if (isBonusTrigger && adaptedRound) {
        const triggerScreen =
          featureState.data?.triggerScreen ||
          adaptedRound.reels ||
          featureState.data?.initialRespinScreen;
        const coinPositions = collectTriggerCoinWinPositions(triggerScreen);
        if (coinPositions.length > 0) {
          const existingWins = (adaptedRound.wins || []).filter((w) => !w.isCoin);
          adaptedRound.wins = [...existingWins, buildSyntheticCoinWin(coinPositions)];
        }
      }

      if (adaptedRound) {
        adaptedRound.triggerWin = adaptedRound.win > 0 || isBonusTrigger;
      }

      this._advanceReplayStep(stepId);
      // Checked AFTER advancing — true once this was the last recorded step,
      // the signal replaySlice/ReelControlContext use to show the Ended
      // modal instead of allowing another spin (docs/replay-history-spec.md
      // §4). Not consumed by anything live-side; harmless extra field there.
      const replayFinished = this._nextReplayStepId() == null;

      return {
        token: this.currentToken,
        // No bet/balance fields on purpose — gameSettingsSlice.js's
        // rgsPlay.fulfilled writes state.game.bet/state.game.balance
        // unconditionally whenever either is present (its balance-strategy
        // comment is aspirational, not actually gated on
        // getBalanceStrategy()'s flags), so returning the replayed round's
        // own bet/balance here would silently overwrite the player's real
        // live bet/balance in Redux — e.g. replaying an old round placed at
        // 5.00 would leave their live bet stuck at 5.00 after exiting, with
        // no restore path. The replay bet/balance display is already
        // handled separately and correctly via replaySlice.replayBet/
        // replayBalance (Stage 1) — roundBet above (used for adaptedRound)
        // covers everything actually needed from this value.
        bet: undefined,
        balance: undefined,
        // This step's wallet under a replay-only field name, so it reaches
        // replaySlice (which shows it) without going near the live
        // `balance` field above that gameSettingsSlice would write straight
        // into the player's real wallet. Verified live (2026-08-10): a step
        // reports the wallet with the bet already deducted and the win not
        // yet credited.
        replayWallet:
          typeof data.balance?.wallet === "number" ? data.balance.wallet : null,
        round: adaptedRound || normalizedOutcome,
        gameMode,
        featureState,
        availableActions: data.flow?.available_actions || ["init", "spin"],
        flowState: data.flow?.state || "ready",
        features: data.features || null,
        // Same passthrough as initFromReplayLink — see its comment. Returning
        // null here isn't neutral: freeRoundsSlice reads null as "FR ended"
        // and clears the counter, so a replayed free round would switch its
        // own indicators off mid-round.
        freeRounds: data.free_rounds || null,
        freeRoundsTotalWin: data.balance?.free_rounds ?? null,
        command: "spin",
        progress: data?.progress || null,
        replayFinished,
      };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) throw error;
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Replay step call failed: ${error.message}`);
    }
  }

  // ── Rounds History (Replay & History panel) ──

  /**
   * Absolute URL for the rounds-history GET, or null when the launcher didn't
   * provide one. The link comes pre-signed in
   * window.__OPTIONS__.actions.history.link and is normally relative, so it
   * resolves against the API origin (`api` is a full URL with path + query,
   * hence origin only). `actions.history.mode` is "api" for this transport.
   * @returns {string|null}
   */
  getRoundsHistoryUrl() {
    const link = window.__OPTIONS__?.actions?.history?.link;
    if (!link || typeof link !== "string") return null;
    if (/^https?:\/\//i.test(link)) return link;

    const api = window.__OPTIONS__?.api;
    if (!api) return null;
    try {
      const origin = new URL(api).origin;
      return `${origin}${link.startsWith("/") ? "" : "/"}${link}`;
    } catch {
      return null;
    }
  }

  /**
   * Pull the replay API base out of a row's `replay_link`.
   * The link is normally a launcher URL carrying a `replayUrl` query param;
   * when there's no `game` param the link itself is the API URL.
   */
  _extractReplayUrl(replayLink) {
    if (!replayLink || typeof replayLink !== "string") return null;
    try {
      const url = new URL(replayLink);
      const explicit = url.searchParams.get("replayUrl");
      if (explicit) return explicit;
      return url.searchParams.get("game") ? null : replayLink;
    } catch {
      return null;
    }
  }

  /** Round id carried in the row's `replay_link` query string. */
  _extractRoundId(replayLink) {
    if (!replayLink || typeof replayLink !== "string") return null;
    try {
      return new URL(replayLink).searchParams.get("round_id");
    } catch {
      return null;
    }
  }

  /**
   * Fetch the player's past rounds for the History panel.
   *
   * No auth headers are needed — the token is baked into the signed path.
   * Response is either a bare array or an object wrapping `rounds`.
   *
   * Amounts stay in RAW SUBUNITS (formatting happens at render, like
   * state.game). The signed `replayUrl` / `roundId` are re-issued on every
   * call, so results must never be cached.
   *
   * @returns {Promise<Array<object>>} rows in server order (newest first)
   */
  async fetchRoundsHistory() {
    const historyUrl = this.getRoundsHistoryUrl();
    if (!historyUrl) {
      throw new Error("PopiPlay: actions.history.link is not configured");
    }

    try {
      const response = await axios.get(historyUrl, {
        headers: { Accept: "application/json" },
      });

      const data = response?.data;
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0];
        throw new Error(`PopiPlay: API error: ${error.desc} (code: ${error.code})`);
      }

      const rawRows = Array.isArray(data) ? data : data?.rounds;
      if (!Array.isArray(rawRows)) return [];

      return rawRows.map((row) => ({
        time: row?.time ?? null,
        bet: Number(row?.bet) || 0,
        win: Number(row?.win) || 0,
        initialBet: Number(row?.initial_bet) || Number(row?.bet) || 0,
        currency: row?.currency ?? null,
        feature: row?.feature ?? null,
        roundId: this._extractRoundId(row?.replay_link),
        replayUrl: this._extractReplayUrl(row?.replay_link),
        // Full shareable launcher URL, kept as-is (unlike replayUrl above,
        // which is just the API base extracted out of it) — needed for the
        // Ended modal's "Copy replay link" (spec §6.3).
        replayLink: row?.replay_link ?? null,
      }));
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`PopiPlay: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("PopiPlay:")) throw error;
      throw new Error(`PopiPlay: Rounds history API call failed: ${error.message}`);
    }
  }

  // ── Spin ──

  spin(bet, token, command, purchasedFeature, spinOptions) {
    // Replay branch (docs/replay-history-spec.md §0, Stage 2) — every param
    // above comes from live state (state.game/state.freeRounds via
    // getSpinParams), all irrelevant here: the next replay step is fixed by
    // the recording, not by what a live spin would have asked for. See
    // _replaySpin() for what this actually does instead.
    if (this.replayMode) {
      return this._replaySpin();
    }

    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(new Error("PopiPlay: window.__OPTIONS__ not found."));
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(new Error(`PopiPlay: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`));
        return;
      }

      this.worker.onmessage = (event) => {
        const data = event.data;

        if (data.error) {
          let errorCode = null;
          if (typeof data.error === "object" && data.error.errors && Array.isArray(data.error.errors)) {
            errorCode = data.error.errors[0]?.code;
          }

          if (errorCode && PROVIDER_CONFIG.ERROR_CODES[errorCode] !== undefined) {
            errorCode = PROVIDER_CONFIG.ERROR_CODES[errorCode];
            if (data.error.errors && data.error.errors[0]) {
              data.error.errors[0].code = errorCode;
            }
          }

          let extractedBalance = null;
          if (typeof data.error === "object" && data.error.balance) {
            if (typeof data.error.balance === "object" && data.error.balance.wallet !== undefined) {
              extractedBalance = data.error.balance.wallet;
            } else if (typeof data.error.balance === "number") {
              extractedBalance = data.error.balance;
            }
          }

          if (extractedBalance !== null && typeof data.error === "object") {
            data.error.balance = extractedBalance;
          }

          if (typeof data.error === "object" && data.error.errors) {
            reject(data.error);
          } else {
            const normalizedError = {
              errors: [{ code: errorCode || 999, desc: typeof data.error === "string" ? data.error : "Unknown error" }],
            };
            if (extractedBalance !== null) normalizedError.balance = extractedBalance;
            reject(normalizedError);
          }
          return;
        }

        // Keep raw screen before normalization (needed for bonus symbol parsing)
        const rawScreen = data.outcome?.screen
          ? data.outcome.screen.map((reel) => [...reel])
          : null;

        // Normalize symbol IDs
        if (data.outcome && Array.isArray(data.outcome.screen)) {
          const normalized = normalizeScreenData(data.outcome.screen);
          data.outcome.reels = normalized;
          data.outcome.screen = normalized;
        }
        const transformedData = data;

        if (transformedData.flow?.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        let extractedBalance = transformedData.balance;
        if (transformedData.balance && typeof transformedData.balance === "object" && transformedData.balance.wallet !== undefined) {
          extractedBalance = transformedData.balance.wallet;
        }

        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(transformedData.outcome);
          // Use balance.game as the win value — same as outcome.win but persists
          // during bonus respins (outcome.win resets to 0, balance.game accumulates)
          normalizedOutcome.win = transformedData.balance?.game ?? normalizedOutcome.win;
        }

        // Forward anticipation flag
        const anticipation = transformedData.features?.anticipation ?? transformedData.anticipation;
        if (anticipation !== undefined) {
          normalizedOutcome.anticipation = anticipation;
        }

        if (normalizedOutcome && transformedData.flow?.round_id) {
          normalizedOutcome.id = transformedData.flow.round_id;
        }

        const isRespin = command === "respin";
        let featureState = this.detectFeatureState(transformedData);

        // Bonus game: attach currentRound for trigger/respin
        const isBonusTrigger =
          featureState.type === "bonus" &&
          featureState.phase === "intro" &&
          command === "spin";
        const isBonusRespin =
          featureState.type === "bonus" && command === "respin";

        if (featureState.type === "bonus" && (isBonusTrigger || isBonusRespin)) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            featureState.data.currentRound = {
              reels: normalizedReels,
              wins: normalizedOutcome.wins || [],
              win: normalizedOutcome.win || 0,
              bet: normalizedOutcome.bet,
              storage: transformedData.outcome?.storage || null,
            };
          }
        }

        const gameMode = this.getGameMode(featureState);

        // Balance: trigger spin defers win (added at bonus end)
        const winAmount = normalizedOutcome?.win || 0;
        let finalBalance;
        if (isBonusTrigger) {
          finalBalance = extractedBalance;
        } else {
          finalBalance = extractedBalance + winAmount;
        }

        const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, bet);

        // On trigger: inject synthetic coin win for ALL coins/Zeus on base trigger screen
        // (same helper as init reconstruct — matches what LS stores for win replay)
        if (isBonusTrigger && adaptedRound) {
          const triggerScreen =
            featureState.data?.triggerScreen ||
            adaptedRound.reels ||
            featureState.data?.initialRespinScreen;
          const coinPositions = collectTriggerCoinWinPositions(triggerScreen);
          if (coinPositions.length > 0) {
            const existingWins = (adaptedRound.wins || []).filter((w) => !w.isCoin);
            const coinWin = buildSyntheticCoinWin(coinPositions);
            adaptedRound.wins = [...existingWins, coinWin];
          }
        }

        // triggerWin: single flag for useWinController — true when there's any win or a bonus trigger
        if (adaptedRound) {
          adaptedRound.triggerWin = (adaptedRound.win > 0) || isBonusTrigger;
        }

        const gameState = {
          balance: finalBalance,
          bet: transformedData.outcome?.bet || null,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
        };

        this.saveGameState(gameState);

        // ── Trigger spin: save triggerWin immediately ──
        // Capture the base game win that triggered the bonus BEFORE any respins.
        // This is frozen for the entire bonus — respins never overwrite it.
        if (isBonusTrigger) {
          const baseTriggerWin = adaptedRound?.win ?? 0;
          StorageManager.saveTriggerWin(baseTriggerWin);
        }

        // Save bonus game round to localStorage on each respin
        if (isBonusRespin && featureState.data?.currentRound) {
          // Safety: only strip + save if this is a confirmed valid bonus respin response.
          // Requires phase=in_progress, a non-empty reels grid, and respinsLeft defined.
          // This prevents accidental stripping on malformed/partial responses.
          const isValidBonusRespinResponse =
            featureState.phase === "in_progress" &&
            Array.isArray(featureState.data.currentRound.reels) &&
            featureState.data.currentRound.reels.length > 0 &&
            featureState.data.respinsLeft !== undefined;

          if (isValidBonusRespinResponse) {
            const isFirstRespin = !StorageManager.hasBonusGameRound();
            // On first respin, strip wins from base game round so reload won't replay win animations
            if (isFirstRespin) {
              StorageManager.stripBaseGameWins();
            }
            const respinsLeft = featureState.data?.respinsLeft ?? 0;
            StorageManager.saveBonusGameRound({
              round: featureState.data.currentRound,
              bet: gameState.bet,
              respinsLeft,
              respinsIssued: featureState.data?.respinsIssued ?? 3,
              // totalWin: only needed for completion reload — set on last respin only
              totalWin: respinsLeft === 0 ? (gameState.round?.win ?? 0) : 0,
              coinCount: featureState.data?.coinCount ?? 0,
              respinGameType: featureState.data?.respinGameType || "",
              initialRespinScreen: featureState.data?.initialRespinScreen || null,
              initialRespinValues: featureState.data?.initialRespinValues || null,
              initialRespinOriginalValues: featureState.data?.initialRespinOriginalValues || null,
              initialEvents: featureState.data?.initialEvents || [],
              triggerCoordinates: featureState.data?.triggerCoordinates || null,
              valuesGrid: featureState.data?.valuesGrid || null,
              originalValues: featureState.data?.originalValues || null,
              prevScreen: featureState.data?.prevScreen || null,
              screenProgress: featureState.data?.screenProgress || [],
              // Accumulate flippedCoinPositions: load previous + process current events
              flippedCoinPositions: this._accumulateFlippedPositions(featureState),
              // Win limit flag — BE source of truth
              winLimitReached: featureState.data?.winLimitReached || false,
            });
          }
        }
        // NOTE: stripBaseGameWins() is intentionally NOT called here on isBonusTrigger.
        // Until the first actual respin is saved, every reload must land on intro phase
        // and replay the trigger spin win animation. The strip happens on the FIRST respin
        // above (inside the isBonusRespin block, guarded by !hasBonusGameRound()).

        if (command === "spin" && gameMode === "base") {
          // Always clear bonus game round on base game spin
          StorageManager.clearBonusGameRound();
          // Only clear free rounds data when FR is finished —
          // check localStorage since API response format may vary
          const currentFR = StorageManager.loadFreeRoundsRound();
          if (!currentFR || currentFR.status !== "active") {
            StorageManager.clearFreeRoundsRound();
          }
        }

        // ── Free Rounds from spin response ──
        const freeRoundsData = transformedData.free_rounds || null;
        const freeRoundsTotalWin = transformedData.balance?.free_rounds ?? null;

        // Save FR state to localStorage on each FR spin (for reload recovery)
        if (freeRoundsData) {
          if (StorageManager.hasFreeRoundsRound()) {
            // Exists → only update changing fields, never touch betToRestore
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? StorageManager.loadFreeRoundsRound()?.win ?? 0,
              // This round's own win is NOT folded into `win` yet — that only
              // happens once its reveal plays (WinDisplay → updateFreeRoundsWin).
              // Set immediately, synchronously, so a reload right here already
              // knows the persisted total is pre-reveal.
              lastRoundApplied: false,
            });
          } else {
            // Doesn't exist → create with betToRestore from base_game_round
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? 0,
              betToRestore: StorageManager.loadBaseGameRound()?.bet ?? null,
              lastRoundApplied: false,
            });
            // Strip stale base game wins so reload at FR round 0 won't replay
            // the previous base game win animation. Bet is already captured above.
            StorageManager.stripBaseGameWins();
          }
        }

        const result = {
          token: transformedData.flow?.round_id || null,
          bet: gameState.bet,
          balance: finalBalance,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
          availableActions: transformedData.flow?.available_actions || ["init", "spin"],
          flowState: transformedData.flow?.state || "ready",
          features: transformedData.features || null,
          freeRounds: freeRoundsData,
          freeRoundsTotalWin,
          command,
          progress: transformedData.progress || null,
        };

        // Attach raw respin round for bonus grid rendering
        if (isRespin) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            respinRound.reels = normalizeScreenData(originalScreen);
            result.respinRound = respinRound;
          }
        }
        // ── FR cancellation on any spin/respin ──
        // Server indicates FR was revoked. Reject with 206 immediately
        // to trigger error recovery overlay and reset the game cleanly.
        if (freeRoundsData?.status === "canceled") {
          StorageManager.clearFreeRoundsRound();
          StorageManager.clearBonusGameRound();
          reject({
            errors: [{ code: 206, desc: "free_rounds_canceled" }],
          });
          return;
        }

        resolve(result);
      };

      this.worker.onerror = (event) => {
        reject(new Error(`PopiPlay: Worker error: ${event.message || "Unknown worker error"}`));
      };

      const tokenToUse = token || this.currentToken || playToken;
      const purchasedFeatureLevel = spinOptions?.purchased_feature_level || null;

      this.worker.postMessage({
        bet,
        token: tokenToUse,
        apiUrl,
        csrfTokenName: csrfTokenHeaderName,
        csrfTokenValue: csrfTokenHeaderValue,
        identifier,
        command,
        ...(purchasedFeature ? { purchasedFeature } : {}),
        ...(purchasedFeatureLevel ? { purchasedFeatureLevel } : {}),
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  // ── Spin Params ──

  getSpinParams({ bet, featureState, freeRoundsActive, freeRoundsBet, availableActions, purchasedFeature, purchasedLevel }) {
    // ── The respin decision is the server's, not ours ──
    //
    // `respin` used to be sent on local featureState alone. That state can
    // outlive the round: the final respin response still carries
    // features.respins_left and features.trigger_screen, so detectFeatureState
    // keeps reporting type "bonus" after the server has already closed the
    // round. Any spin trigger in that window sent a respin to a closed round,
    // the API answered unavailable_action, and repeated offences got players
    // BANNED (reported by TAM 2026-08-13 — three cases, 173ms / 410ms / 542ms
    // after a legitimate respin, all Android).
    //
    // flow.available_actions is the server telling us what it will accept. It
    // is already parsed into state.game.availableActions on every response and
    // passed in here; it was simply never read. Honouring it makes the illegal
    // command impossible to construct, whatever the UI does upstream.
    const canRespin = !availableActions || availableActions.includes("respin");
    if (featureState?.type === "bonus") {
      if (!canRespin) {
        return { command: "spin", spinOptions: undefined };
      }
      return { command: "respin", spinOptions: undefined };
    }

    // Free rounds: regular spin command with bonus flag and FR bet (same as Dmon)
    if (freeRoundsActive) {
      return { command: "spin", spinOptions: { bonus: true, bet: freeRoundsBet || bet } };
    }

    // Bonus buy: include level so the API knows which tier
    if (purchasedFeature && purchasedLevel) {
      const spinOpts = { purchased_feature_level: String(purchasedLevel) };

      const { DEV_FEATURE_COMBO } = require("../../../devSimulation");
      if (DEV_FEATURE_COMBO) {
        spinOpts.feature_combo = DEV_FEATURE_COMBO;
      }

      return {
        command: "spin",
        spinOptions: spinOpts,
      };
    }

    return { command: "spin", spinOptions: undefined };
  }

  // ── Buy Feature ──

  // Level "1" → random features (80× bet), level "2" → all 3 DMC (300× bet)
  async buyFeature(bet, level = "1") {
    const state = (await import("../../../State/Store/store")).default.getState();
    const token = state.game.token;
    return this.spin(bet, token, "spin", "bonus_buy", {
      purchased_feature_level: String(level),
    });
  }

  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  // ── Balance Strategy ──

  getBalanceStrategy() {
    // Replay (docs/replay-history-spec.md §9, Stage 3): don't touch the live
    // wallet at all — gameSettingsSlice's bet-deduction and balance-merge
    // (rgsPlay.fulfilled) both gate on these flags, so this alone keeps a
    // replay spin from deducting a real bet or merging its win into the
    // real balance, with no changes needed on the slice side.
    if (this.replayMode) {
      return {
        serverIncludesWin: false,
        clientDeductsBet: false,
        clientAddsWin: false,
      };
    }
    return {
      serverIncludesWin: false,
      clientDeductsBet: true,
      clientAddsWin: true,
    };
  }

  shouldSyncBalanceOnWinClose() {
    return true;
  }

  // ── Session Management ──


  getCurrentToken() {
    return this.currentToken || null;
  }

  // ── Feature Detection ──

  // Detect bonus game state from API response.
  // Discriminator: features.respin_game_type present + flow.state === "respin"
  // Phase detection:
  //   "intro"       — bonus triggered, no respins played yet (storage is null)
  //   "in_progress" — mid-bonus, respins already played (storage has data)
  //   "outro"       — completion phase (future)
  detectFeatureState(apiResponse) {
    const { flow, features } = apiResponse;

    if (
      features?.respins_left !== undefined &&
      features?.respin_game_type &&
      (flow?.state === "respin" || features.trigger_screen !== undefined)
    ) {
      // Phase detection:
      //   spin command  → always intro (trigger spin from base game)
      //   respin command → always in_progress (actively playing bonus)
      //   init command   → check outcome.storage: null = intro, present = in_progress
      let phase;
      if (flow?.command === "spin") {
        phase = "intro";
      } else if (flow?.command === "respin") {
        phase = "in_progress";
      } else if (flow?.command === "init" && flow?.state === "respin") {
        phase = apiResponse.outcome?.storage ? "in_progress" : "intro";
      } else {
        phase = "intro";
      }

      const storage = apiResponse.outcome?.storage || {};

      // Fix raw multiplier values → actual win amounts using outcome.wins
      const outcomeWins = apiResponse.outcome?.wins || [];
      if (outcomeWins.length > 0) {
        const winLookup = {};
        for (const win of outcomeWins) {
          if (win[0] === "coin" && win.length >= 4) {
            winLookup[`${win[2]}_${win[3]}`] = win[1];
          }
        }

        if (storage.values && Array.isArray(storage.values)) {
          for (let ri = 0; ri < storage.values.length; ri++) {
            if (!Array.isArray(storage.values[ri])) continue;
            for (let si = 0; si < storage.values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                storage.values[ri][si] = winLookup[key];
              }
            }
          }
        }

        if (features.initial_respin_values && Array.isArray(features.initial_respin_values)) {
          for (let ri = 0; ri < features.initial_respin_values.length; ri++) {
            if (!Array.isArray(features.initial_respin_values[ri])) continue;
            for (let si = 0; si < features.initial_respin_values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                features.initial_respin_values[ri][si] = winLookup[key];
              }
            }
          }
        }
      }

      // Detect new coins: diff prev_screen vs outcome.screen (blank → non-blank)
      let newCoins = [];
      if (phase === "in_progress") {
        const prevScreen = storage.prev_screen || normalizeScreenData(features.initial_respin_screen) || [];
        const currentScreen = apiResponse.outcome?.screen || [];
        for (let ri = 0; ri < currentScreen.length; ri++) {
          if (!Array.isArray(currentScreen[ri])) continue;
          for (let si = 0; si < currentScreen[ri].length; si++) {
            const prevId = parseInt(prevScreen?.[ri]?.[si], 10) || 0;
            const currId = parseInt(currentScreen[ri][si], 10) || 0;
            if (prevId === BLANK_SYMBOL_ID && currId !== BLANK_SYMBOL_ID) {
              let zeusFeature = null;
              if (currId === ZEUS_MYSTERY_ID || currId === CM_MINOR_ID || currId === CM_MINI_ID || currId === CM_MAJOR_ID) {
                zeusFeature = "M";
              } else if (currId === ZEUS_DOUBLE_ID || currId === CD_MULT_ID) {
                zeusFeature = "D";
              } else if (currId === ZEUS_COLLECT_ID) {
                zeusFeature = "C";
              }
              newCoins.push({
                reelIndex: ri,
                symbolIndex: si,
                symbolId: currId,
                isZeus: zeusFeature !== null,
                zeusFeature,
              });
            }
          }
        }
      }

      // Build events list (new API: events/initial_events, legacy: screen_progress)
      const rawScreenProgress = phase === "intro"
        ? [...(features.initial_events || features.initial_screen_progress || []), ...(storage.events || storage.screen_progress || [])]
        : (storage.events || storage.screen_progress || []);

      return {
        type: "bonus",
        phase,
        data: {
          respinsLeft: features.respins_left,
          respinsIssued: features.respins_issued ?? 3,
          respinGameType: features.respin_game_type,
          triggerScreen: features.trigger_screen,
          triggerCoordinates: features.trigger_coordinates || null,
          initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
          initialRespinValues: features.initial_respin_values || null,
          screenProgress: this._normalizeScreenProgress(rawScreenProgress),
          coinValues: storage.coin_values || null,
          prevScreen: storage.prev_screen || null,
          valuesGrid: storage.values || null,
          originalValues: storage.original_values || null,
          initialRespinOriginalValues: features.initial_respin_original_values || null,
          newCoins,
          // Coin count: use API field when available, fallback to counting non-blanks
          // initial_respin_screen has zeus coins (non-16) — use on trigger/init
          // outcome.screen is the bonus grid only on respin (on trigger/init it's the base game screen)
          coinCount: storage.coins_count
            ?? features.initial_coins_count
            ?? features.coin_count
            ?? 0,
          winLimitReached: (() => {
            if (storage.win_limit_reached) return true;
            const totalWin = apiResponse.balance?.game || 0;
            const betAmount = apiResponse.outcome?.bet || 0;
            const maxMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
            return betAmount > 0 && totalWin >= betAmount * maxMultiplier;
          })(),
          currentRound: apiResponse.outcome ? {
            reels: apiResponse.outcome.screen || [],
            wins: apiResponse.outcome.wins || [],
            win: apiResponse.balance?.game || 0,
            storage,
          } : null,
          // The activation event log for the trigger spin — used to determine which mystery
          // coins were actually revealed (m_reveals) during the initial activation sequence.
          initialEvents: features.initial_events || features.initial_screen_progress || [],
        },
      };
    }

    return { type: null, phase: null, data: null };
  }

  // Lightweight bonus detection for reload (used by collectFeatureStates)
  detectBonusFromApi(apiResponse) {
    const { flow, features, outcome } = apiResponse;
    if (
      features?.respins_left === undefined ||
      !features?.respin_game_type ||
      (flow?.state !== "respin" && features?.trigger_screen === undefined)
    ) {
      return null;
    }
    let phase;
    if (flow?.command === "spin") {
      phase = "intro";
    } else if (flow?.command === "respin") {
      phase = "in_progress";
    } else if (flow?.command === "init" && flow?.state === "respin") {
      phase = outcome?.storage ? "in_progress" : "intro";
    } else {
      phase = "intro";
    }
    const storage = outcome?.storage || {};
    return {
      phase,
      data: {
        respinsLeft: features.respins_left,
        respinsIssued: features.respins_issued ?? 3,
        respinGameType: features.respin_game_type,
        triggerScreen: features.trigger_screen,
        triggerCoordinates: features.trigger_coordinates || null,
        initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
        initialRespinValues: features.initial_respin_values || null,
        screenProgress: this._normalizeScreenProgress(
            phase === "intro"
              ? [
                ...(features.initial_events || features.initial_screen_progress || []),
                ...(storage.events || storage.screen_progress || []),
              ]
              : (storage.events || storage.screen_progress || [])
          ),
        coinValues: storage.coin_values || null,
        originalValues: storage.original_values || null,
        initialRespinOriginalValues: features.initial_respin_original_values || null,
        // The activation event log for the trigger spin — used to determine which mystery
        // coins were actually revealed (m_reveals) during the initial activation sequence.
        // Mystery coins NOT in this list are still "raw" and must show as jackpot coins on reload.
        initialEvents: features.initial_events || features.initial_screen_progress || [],
      },
    };
  }

  // ── Screen Progress Helpers ──

  /**
   * Accumulate flippedCoinPositions across respins.
   * Loads the previous set from localStorage, then processes the CURRENT round's
   * events (d_doubles, c_collect) to add any newly flipped positions.
   * For the first save (trigger intro), processes initial_events instead.
   */
  _accumulateFlippedPositions(featureState) {
    const BLANK_ID = 16;
    const data = featureState.data || {};

    // Load previously accumulated flipped positions
    const savedRound = StorageManager.loadBonusGameRound();
    const existing = new Set(savedRound?.flippedCoinPositions || []);

    // Determine which events to process:
    // - First save (no previous round): process initial_events (trigger) + current storage.events
    // - Subsequent saves: only current round's storage.events (prior flips are in localStorage)
    const isFirstSave = !savedRound;
    const rawStorageEvts = data.currentRound?.storage?.events || data.currentRound?.storage?.screen_progress || [];
    const rawEvents = isFirstSave
      ? [...(data.initialEvents || []), ...rawStorageEvts]
      : rawStorageEvts;

    // Normalize events so both cells[] and coordinates[] formats produce [reel, row] pairs
    const events = this._normalizeScreenProgress(rawEvents);

    // Build onBoard: prevScreen (all coins before this spin) + new coins from this round
    const onBoard = new Set();
    if (!isFirstSave) {
      // Seed from prevScreen
      const ps = data.prevScreen || data.currentRound?.storage?.prev_screen || [];
      for (let ri = 0; ri < ps.length; ri++) {
        if (!Array.isArray(ps[ri])) continue;
        for (let si = 0; si < ps[ri].length; si++) {
          const cellId = parseInt(ps[ri][si], 10);
          if (!isNaN(cellId) && cellId !== BLANK_ID) {
            onBoard.add(`${ri}_${si}`);
          }
        }
      }
    }

    // Process normalized events (each has coordinates: [reel, row])
    for (const event of events) {
      const eventType = event.type || event.action;
      const coords = event.coordinates;
      if (!Array.isArray(coords)) continue;
      const pairs = Array.isArray(coords[0]) ? coords : [coords];

      if (eventType === 'new_coins' || eventType === 'm_reveals') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) onBoard.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'd_doubles') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) existing.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'c_collect') {
        for (const key of onBoard) existing.add(key);
      }
    }

    const result = [...existing];
    return result;
  }


  // Normalize screen_progress coordinate formats to consistent flat pairs
  // and split batched coordinates into individual actions
  _normalizeScreenProgress(rawProgress) {
    if (!rawProgress || !Array.isArray(rawProgress)) return [];

    const EXPAND_ACTIONS = new Set(["new_coins", "m_reveals", "d_reveals", "c_collect"]);

    const normalized = [];
    for (const rawAction of rawProgress) {
      const actionType = rawAction.type || rawAction.action;

      // ── NEW FORMAT: cells array ──
      if (rawAction.cells && Array.isArray(rawAction.cells)) {
        if (EXPAND_ACTIONS.has(actionType)) {
          // Split each cell into an individual action (1 event per cell)
          for (const cell of rawAction.cells) {
            normalized.push({
              action: actionType,
              coordinates: [cell.reel, cell.row],
              value: cell.value ?? 0,
              ...(cell.tier ? { tier: cell.tier } : {}),
            });
          }
        } else {
          // d_doubles: keep as array of pairs (batch operation)
          normalized.push({
            action: actionType,
            coordinates: rawAction.cells.map(c => [c.reel, c.row]),
            cells: rawAction.cells, // preserve full cells for value access
          });
        }
        continue;
      }

      // ── LEGACY FORMAT: coordinates array (backward compat) ──
      const action = rawAction.type && !rawAction.action
        ? { ...rawAction, action: rawAction.type }
        : rawAction;

      const coords = action.coordinates;
      if (!Array.isArray(coords) || coords.length === 0) {
        normalized.push(action);
        continue;
      }

      if (EXPAND_ACTIONS.has(action.action) && Array.isArray(coords[0])) {
        // Array-of-pairs: [[0,3],[0,4]] → split into individual actions
        for (const coord of coords) {
          normalized.push({ ...action, coordinates: coord });
        }
      } else {
        normalized.push(action);
      }
    }
    return normalized;
  }

  // ── Game Mode ──

  getGameMode(featureState) {
    if (featureState.type && featureState.phase === "intro") return "base";
    return featureState.type ? "feature" : "base";
  }

  // ── Storage ──

  saveGameState(gameState) {
    const { gameMode, round, bet, balance } = gameState;
    if (gameMode === "base") {
      StorageManager.saveBaseGameRound({ round, bet, balance });
    }
  }

  // ── Reload ──

  collectFeatureStates(apiResponse) {
    return {
      bonus: {
        api: this.detectBonusFromApi(apiResponse),
      },
    };
  }

  determineFeaturePriority(features) {
    if (features.bonus.api) {
      return { primary: "bonus" };
    }
    return { primary: "base" };
  }

  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    if (savedBaseGame) {
      return { round: savedBaseGame.round, bet: savedBaseGame.bet, reloadState: { needsReload: false } };
    }
    return { reloadState: { needsReload: false } };
  }

  loadGameState(apiResponse) {
    this.collectFeatureStates(apiResponse);
    // PC reload is handled by potCollectionSlice via featureState
    return this.buildBaseGameReload();
  }

  clearCompletionData() {
    // Clear bonus game round data on first base game spin after bonus
    StorageManager.clearBonusGameRound();
    // Clear free rounds data on first base game spin after FR completion
    StorageManager.clearFreeRoundsRound();
  }

  extractRoundFromInit(apiResponse) {
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      return { reels: normalizeScreenData(screen), wins: [], win: 0 };
    }
    return null;
  }
}

export { spinWorkerCode };

export default PopiPlayRGS;
